import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, ImagePlus, Upload } from 'lucide-react';
import { GST_RATES, PROCUREMENT_STATUS, dateTimeLabel, itemMarginPct, money } from '@adisys/shared';
import { api, ApiRequestError, downloadCsv } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { Fact, Notice, errorText, qtyLabel, useOpenFile } from '../../../lib/crm';
import { Button, Checkbox, ErrorState, Field, Input, Modal, Select, Skeleton, StatusBadge, Textarea, useToast } from '../../../components/ui';
import { Thumb } from './Thumb';

const refreshCatalog = (qc: ReturnType<typeof useQueryClient>) => { void qc.invalidateQueries({ queryKey: ['crm', 'catalog'] }); };

// ---------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------
export function ProductDetailModal({ id, onClose, onEdit, onAdjust, onDeleted }: {
  id: string | null; onClose: () => void; onEdit: (p: any) => void; onAdjust: (p: any) => void; onDeleted: () => void;
}) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const openFile = useOpenFile();
  const imgInput = useRef<HTMLInputElement>(null);
  const dsInput = useRef<HTMLInputElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const manage = can('crm.catalog.manage');

  const q = useQuery({
    queryKey: ['crm', 'catalog', 'product', id],
    queryFn: () => api.get(`/crm/products/${id}`),
    enabled: Boolean(id),
  });
  const p = q.data;
  const showCost = p && p.costPrice !== undefined;

  const upload = useMutation({
    mutationFn: ({ kind, file }: { kind: 'image' | 'datasheet'; file: File }) => {
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file);
      return api.upload(`/crm/products/${id}/assets`, form);
    },
    onSuccess: () => { toast.success('File uploaded'); refreshCatalog(qc); },
    onError: (e) => toast.error('Upload failed', errorText(e)),
  });
  const del = useMutation({
    mutationFn: () => api.del(`/crm/products/${id}`),
    onSuccess: () => { toast.success('Product removed'); refreshCatalog(qc); setConfirmDelete(false); onDeleted(); },
    onError: (e) => { toast.error('Could not remove the product', errorText(e)); setConfirmDelete(false); },
  });
  const pick = (kind: 'image' | 'datasheet') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) upload.mutate({ kind, file });
  };

  const status = p ? PROCUREMENT_STATUS.byValue[p.procurementStatus as keyof typeof PROCUREMENT_STATUS.byValue] : null;

  return (
    <Modal open={Boolean(id)} onClose={onClose} size="lg" title={p?.name ?? 'Product'} description={p ? `${p.sku}${p.modelNumber ? ` · ${p.modelNumber}` : ''}` : undefined}
      footer={p && (manage || can('crm.procurement.manage')) ? <>
        {can('crm.catalog.manage', 'crm.procurement.manage') && <Button onClick={() => onAdjust(p)}>Adjust stock</Button>}
        {manage && <Button variant="danger" onClick={() => setConfirmDelete(true)}>Remove</Button>}
        {manage && <Button variant="primary" onClick={() => onEdit(p)}>Edit product</Button>}
      </> : undefined}>
      {q.isLoading && <Skeleton className="h-48 w-full" />}
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      {p && (
        <div className="space-y-5">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="space-y-2">
              <Thumb fileId={p.imageFileId} size={144} />
              {manage && (
                <>
                  <input ref={imgInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick('image')} />
                  <Button size="sm" block icon={<ImagePlus className="h-3.5 w-3.5" />} loading={upload.isPending} onClick={() => imgInput.current?.click()}>
                    {p.imageFileId ? 'Replace image' : 'Add image'}
                  </Button>
                </>)}
            </div>
            <dl className="grid flex-1 gap-x-6 gap-y-3 sm:grid-cols-3">
              <Fact label="Category">{p.categoryName}{p.subcategoryName ? ` › ${p.subcategoryName}` : ''}</Fact>
              <Fact label="Brand">{p.brandName}</Fact>
              <Fact label="Vendor">{p.vendorName}</Fact>
              <Fact label="Selling price">{money(p.sellingPrice)} / {p.unit}</Fact>
              <Fact label="GST">{p.gstRate}%</Fact>
              <Fact label="HSN code">{p.hsnCode}</Fact>
              {showCost && <Fact label="Cost price">{money(p.costPrice)}</Fact>}
              {showCost && <Fact label="Margin">{p.marginPct}%</Fact>}
              <Fact label="Status">
                <span className="flex flex-wrap gap-1.5">
                  {status && <StatusBadge tone={status.tone} title={status.description}>{status.label}</StatusBadge>}
                  {!p.active && <StatusBadge>Inactive</StatusBadge>}
                </span>
              </Fact>
              <Fact label="On hand">{qtyLabel(p.stockOnHand)}</Fact>
              <Fact label="Reserved">{qtyLabel(p.stockReserved)}</Fact>
              <Fact label="Available">{qtyLabel(p.available)} (reorder at {qtyLabel(p.reorderLevel)})</Fact>
            </dl>
          </div>

          <div>
            <h4 className="text-xs font-medium text-ink-500">Specifications</h4>
            <p className="mt-1 whitespace-pre-wrap text-sm text-ink-800">{p.specifications || <span className="text-ink-400">None recorded.</span>}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {p.datasheetFileId
              ? <Button size="sm" icon={<FileText className="h-3.5 w-3.5" />} onClick={() => void openFile(p.datasheetFileId)}>Open datasheet</Button>
              : <span className="text-xs text-ink-500">No datasheet uploaded.</span>}
            {manage && (
              <>
                <input ref={dsInput} type="file" accept="application/pdf" className="hidden" onChange={pick('datasheet')} />
                <Button size="sm" icon={<Upload className="h-3.5 w-3.5" />} loading={upload.isPending} onClick={() => dsInput.current?.click()}>
                  {p.datasheetFileId ? 'Replace datasheet' : 'Upload datasheet (PDF)'}
                </Button>
              </>)}
          </div>

          <div>
            <h4 className="mb-1.5 text-xs font-medium text-ink-500">Recent stock movements</h4>
            {p.movements.length === 0 ? <p className="text-xs text-ink-400">No movements yet.</p> : (
              <div className="overflow-x-auto rounded-lg ring-1 ring-line">
                <table className="w-full min-w-[480px] text-left text-xs">
                  <thead className="bg-sunken/60 text-ink-600"><tr>
                    <th className="px-3 py-2">When</th><th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2 text-right">On hand</th><th className="px-3 py-2 text-right">Reserved</th><th className="px-3 py-2">Note</th></tr></thead>
                  <tbody className="divide-y divide-line">
                    {p.movements.map((m: any) => (
                      <tr key={m.id}>
                        <td className="whitespace-nowrap px-3 py-1.5">{dateTimeLabel(m.createdAt)}</td>
                        <td className="px-3 py-1.5 capitalize">{m.kind}</td>
                        <td className="tabular px-3 py-1.5 text-right">{Number(m.onHandDelta) > 0 ? '+' : ''}{qtyLabel(m.onHandDelta)}</td>
                        <td className="tabular px-3 py-1.5 text-right">{Number(m.reservedDelta) > 0 ? '+' : ''}{qtyLabel(m.reservedDelta)}</td>
                        <td className="px-3 py-1.5 text-ink-600">{m.note ?? '—'}{m.actorName ? ` · ${m.actorName}` : ''}</td>
                      </tr>))}
                  </tbody>
                </table>
              </div>)}
          </div>
        </div>
      )}

      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} size="sm" title="Remove product"
        footer={<><Button onClick={() => setConfirmDelete(false)}>Cancel</Button><Button variant="danger" loading={del.isPending} onClick={() => del.mutate()}>Remove</Button></>}>
        <p className="text-sm text-ink-700">Remove <strong>{p?.name}</strong> from the catalogue? Existing BOQs and orders keep their own copy of the product details.</p>
      </Modal>
    </Modal>
  );
}

// ---------------------------------------------------------------------
// Create / edit
// ---------------------------------------------------------------------
const blank = {
  sku: '', name: '', modelNumber: '', categoryId: '', subcategoryId: '', brandId: '', vendorId: '', specifications: '',
  unit: 'Nos', hsnCode: '', costPrice: '0', sellingPrice: '0', gstRate: '18', reorderLevel: '0', openingStock: '0',
  discontinued: false, active: true,
};

export function ProductFormModal({ product, open, onClose, categories, vendors, showCost, onSaved }: {
  product: any | null; open: boolean; onClose: () => void; categories: any[]; vendors: any[];
  /** Cost fields are only offered to people the API already sends cost to. */
  showCost: boolean; onSaved: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const editing = Boolean(product);
  const [form, setForm] = useState({ ...blank });
  const [error, setError] = useState<ApiRequestError | null>(null);
  const [seen, setSeen] = useState<string | null>(null);

  // Re-seed the form each time the modal opens for a different product.
  const key = open ? (product?.id ?? 'new') : null;
  if (key !== seen) {
    setSeen(key);
    if (key) {
      setError(null);
      setForm(product ? {
        sku: product.sku, name: product.name, modelNumber: product.modelNumber ?? '', categoryId: product.categoryId,
        subcategoryId: product.subcategoryId ?? '', brandId: product.brandId ?? '', vendorId: product.vendorId ?? '',
        specifications: product.specifications ?? '', unit: product.unit, hsnCode: product.hsnCode ?? '',
        costPrice: String(product.costPrice ?? 0), sellingPrice: String(product.sellingPrice), gstRate: String(product.gstRate),
        reorderLevel: String(product.reorderLevel), openingStock: '0', discontinued: product.discontinued, active: product.active,
      } : { ...blank });
    }
  }

  const cat = categories.find((c) => c.id === form.categoryId);
  const set = (k: keyof typeof blank) => (e: any) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fe = (k: string) => error?.fieldError(k);
  const cost = Number(form.costPrice), price = Number(form.sellingPrice);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name: form.name.trim(), modelNumber: form.modelNumber.trim() || null, categoryId: form.categoryId,
        subcategoryId: form.subcategoryId || null, brandId: form.brandId || null, vendorId: form.vendorId || null,
        specifications: form.specifications.trim() || null, unit: form.unit.trim() || 'Nos', hsnCode: form.hsnCode.trim() || null,
        sellingPrice: Number(form.sellingPrice), gstRate: Number(form.gstRate), reorderLevel: Number(form.reorderLevel),
        discontinued: form.discontinued, active: form.active,
        ...(showCost ? { costPrice: Number(form.costPrice) } : {}),
      };
      if (form.sku.trim()) body.sku = form.sku.trim();
      if (!editing) body.openingStock = Number(form.openingStock);
      return editing ? api.patch(`/crm/products/${product.id}`, body) : api.post('/crm/products', body);
    },
    onSuccess: () => { toast.success(editing ? 'Product updated' : 'Product created'); refreshCatalog(qc); onSaved(); onClose(); },
    onError: (e) => { if (e instanceof ApiRequestError) setError(e); toast.error('Could not save the product', errorText(e)); },
  });

  return (
    <Modal open={open} onClose={onClose} size="xl" title={editing ? `Edit ${product?.name}` : 'New product'}
      footer={<><Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} disabled={!form.name.trim() || !form.categoryId} onClick={() => save.mutate()}>
          {editing ? 'Save changes' : 'Create product'}</Button></>}>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Product name" required error={fe('name')} className="sm:col-span-2"><Input value={form.name} onChange={set('name')} invalid={!!fe('name')} /></Field>
        <Field label="SKU" error={fe('sku')} hint={editing ? undefined : 'Leave blank to generate one'}><Input value={form.sku} onChange={set('sku')} className="uppercase" invalid={!!fe('sku')} /></Field>
        <Field label="Model number" error={fe('modelNumber')}><Input value={form.modelNumber} onChange={set('modelNumber')} /></Field>
        <Field label="Category" required error={fe('categoryId')}>
          <Select value={form.categoryId} onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value, subcategoryId: '', brandId: '' }))} invalid={!!fe('categoryId')}>
            <option value="">Choose…</option>
            {categories.filter((c) => c.active || c.id === form.categoryId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Subcategory" error={fe('subcategoryId')}>
          <Select value={form.subcategoryId} onChange={set('subcategoryId')} disabled={!cat}>
            <option value="">None</option>
            {cat?.subcategories.filter((s: any) => s.active || s.id === form.subcategoryId).map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
        <Field label="Brand" error={fe('brandId')}>
          <Select value={form.brandId} onChange={set('brandId')} disabled={!cat} invalid={!!fe('brandId')}>
            <option value="">None</option>
            {cat?.brands.filter((b: any) => b.active || b.id === form.brandId).map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Preferred vendor" error={fe('vendorId')}>
          <Select value={form.vendorId} onChange={set('vendorId')}>
            <option value="">None</option>
            {vendors.filter((v) => v.active || v.id === form.vendorId).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </Select>
        </Field>
        <Field label="Unit" error={fe('unit')}><Input value={form.unit} onChange={set('unit')} maxLength={20} /></Field>
        <Field label="HSN code" error={fe('hsnCode')}><Input value={form.hsnCode} onChange={set('hsnCode')} /></Field>

        {showCost && <Field label="Cost price (₹)" error={fe('costPrice')}><Input type="number" min={0} step="0.01" value={form.costPrice} onChange={set('costPrice')} invalid={!!fe('costPrice')} /></Field>}
        <Field label="Selling price (₹)" error={fe('sellingPrice')}><Input type="number" min={0} step="0.01" value={form.sellingPrice} onChange={set('sellingPrice')} invalid={!!fe('sellingPrice')} /></Field>
        <Field label="GST rate" error={fe('gstRate')}>
          <Select value={form.gstRate} onChange={set('gstRate')}>
            {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
          </Select>
        </Field>
        {showCost && (
          <div className="flex items-end pb-2 text-sm text-ink-700">
            Margin: <strong className="ml-1.5 tabular text-ink-900">{price > 0 ? `${itemMarginPct(cost, price)}%` : '—'}</strong>
          </div>)}
        <Field label="Reorder level" error={fe('reorderLevel')} hint="Flagged low stock at or below this"><Input type="number" min={0} value={form.reorderLevel} onChange={set('reorderLevel')} /></Field>
        {!editing && <Field label="Opening stock" error={fe('openingStock')}><Input type="number" min={0} value={form.openingStock} onChange={set('openingStock')} /></Field>}

        <Field label="Specifications" error={fe('specifications')} className="sm:col-span-3">
          <Textarea rows={4} value={form.specifications} onChange={set('specifications')} placeholder="Resolution, lens, IP rating, ports, power…" />
        </Field>
        <div className="flex flex-wrap gap-6 sm:col-span-3">
          <Checkbox label="Active" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
          <Checkbox label="Discontinued" description="Cannot be added to new BOQs" checked={form.discontinued} onChange={(e) => setForm((f) => ({ ...f, discontinued: e.target.checked }))} />
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------
// Stock adjustment
// ---------------------------------------------------------------------
export function StockAdjustModal({ product, onClose }: { product: any | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<ApiRequestError | null>(null);

  const save = useMutation({
    mutationFn: () => api.post(`/crm/products/${product.id}/stock-adjustment`, { delta: Number(delta), reason: reason.trim() }),
    onSuccess: () => { toast.success('Stock adjusted'); refreshCatalog(qc); setDelta(''); setReason(''); onClose(); },
    onError: (e) => { if (e instanceof ApiRequestError) setError(e); toast.error('Could not adjust stock', errorText(e)); },
  });
  const n = Number(delta);

  return (
    <Modal open={Boolean(product)} onClose={onClose} size="sm" title="Adjust stock" description={product ? `${product.name} · on hand ${qtyLabel(product.stockOnHand)}` : undefined}
      footer={<><Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} disabled={!delta || !Number.isFinite(n) || n === 0 || reason.trim().length < 3} onClick={() => save.mutate()}>Apply</Button></>}>
      <div className="space-y-4">
        <Field label="Change in stock" required error={error?.fieldError('delta')} hint="Use a minus sign to remove stock, e.g. -2 for damaged units">
          <Input type="number" step="any" value={delta} onChange={(e) => setDelta(e.target.value)} invalid={!!error?.fieldError('delta')} />
        </Field>
        <Field label="Reason" required error={error?.fieldError('reason')}><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Stock count, damage, opening balance…" /></Field>
        {product && Number.isFinite(n) && n !== 0 && <p className="text-xs text-ink-500">New on-hand quantity: <strong>{qtyLabel(Number(product.stockOnHand) + n)}</strong></p>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------
// Bulk import
// ---------------------------------------------------------------------
interface ImportResult { applied: boolean; dryRun: boolean; summary: { created: number; updated: number; errors: number }; results: Array<{ row: number; sku: string | null; status: string; message?: string }> }

export function ImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [csv, setCsv] = useState('');
  const [check, setCheck] = useState<ImportResult | null>(null);

  const reset = () => { setFileName(''); setCsv(''); setCheck(null); };
  const close = () => { reset(); onClose(); };

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.post<ImportResult>('/crm/products/import', { csv, dryRun }),
    onSuccess: (r, dryRun) => {
      setCheck(r);
      if (!dryRun && r.applied) {
        toast.success('Import complete', `${r.summary.created} created, ${r.summary.updated} updated.`);
        refreshCatalog(qc); close();
      }
    },
    onError: (e) => toast.error('Import failed', errorText(e)),
  });

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileName(file.name); setCheck(null);
    setCsv(await file.text());
  };

  const clean = Boolean(check && check.dryRun && check.summary.errors === 0);
  const errors = check?.results.filter((r) => r.status === 'error') ?? [];

  return (
    <Modal open={open} onClose={close} size="lg" title="Import products from CSV"
      description="Existing SKUs are updated; new rows are created. Nothing is saved unless the whole file is valid."
      footer={<><Button onClick={close}>Cancel</Button>
        <Button disabled={!csv} loading={run.isPending && run.variables === true} onClick={() => run.mutate(true)}>Check file</Button>
        <Button variant="primary" disabled={!clean} loading={run.isPending && run.variables === false} onClick={() => run.mutate(false)}>Import</Button></>}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input ref={input} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
          <Button icon={<Upload className="h-4 w-4" />} onClick={() => input.current?.click()}>Choose CSV file</Button>
          <span className="text-sm text-ink-600">{fileName || 'No file chosen'}</span>
          <button className="ml-auto text-xs font-medium text-brand-600 hover:underline"
            onClick={() => void downloadCsv('/crm/products-import-template', {}, 'product-import-template.csv').catch((err) => toast.error('Download failed', errorText(err)))}>
            Download template
          </button>
        </div>
        <p className="text-xs text-ink-500">Required columns: name, category. Optional: sku, model_number, subcategory, brand, vendor, specifications, unit, hsn_code, cost_price, selling_price, gst_rate, opening_stock, reorder_level.</p>

        {check && (
          check.summary.errors === 0
            ? <Notice tone="success">File is valid: {check.summary.created} to create, {check.summary.updated} to update. Press Import to apply.</Notice>
            : <Notice tone="danger">{check.summary.errors} row{check.summary.errors === 1 ? ' has' : 's have'} problems. Fix the file and check again; nothing has been saved.</Notice>)}

        {errors.length > 0 && (
          <div className="max-h-64 overflow-auto rounded-lg ring-1 ring-line">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-sunken text-ink-600"><tr><th className="px-3 py-2">Row</th><th className="px-3 py-2">SKU</th><th className="px-3 py-2">Problem</th></tr></thead>
              <tbody className="divide-y divide-line">
                {errors.map((r) => <tr key={r.row}><td className="tabular px-3 py-1.5">{r.row}</td><td className="px-3 py-1.5">{r.sku ?? '—'}</td><td className="px-3 py-1.5 text-danger">{r.message}</td></tr>)}
              </tbody>
            </table>
          </div>)}
      </div>
    </Modal>
  );
}
